'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import pills from './styles.module.css';
import styles from './Hero.module.css';

/* Same 992px breakpoint the page's media queries use. */
const DESKTOP_MIN_WIDTH = 992;

const MOBILE_VIDEO = getCompressedAssetUrl(
  '/web_v2/videos/launch-reel.mp4/w_1920',
);
const DESKTOP_VIDEO = getCompressedAssetUrl(
  '/web_v2/videos/launch-cinema.mp4/w_1920',
);

export const Hero = () => {
  const t = useTranslations('whyUltrahuman');
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const tryPlay = () => {
      video.muted = true;
      video.playsInline = true;
      video.controls = false;
      if (video.paused) {
        void video.play().catch(() => {
          /* Keep controls off — decorative background only. */
        });
      }
    };

    const setVideoForViewport = () => {
      const nextSrc =
        window.innerWidth >= DESKTOP_MIN_WIDTH ? DESKTOP_VIDEO : MOBILE_VIDEO;

      if (!video.src || !video.src.endsWith(nextSrc)) {
        video.src = nextSrc;
        video.load();
      }
      tryPlay();
    };

    setVideoForViewport();
    window.addEventListener('resize', setVideoForViewport);

    const onGesture = () => tryPlay();
    document.addEventListener('touchstart', onGesture);
    document.addEventListener('click', onGesture);

    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) tryPlay();
      else video.pause();
    }, {});
    observer.observe(video);

    return () => {
      window.removeEventListener('resize', setVideoForViewport);
      document.removeEventListener('touchstart', onGesture);
      document.removeEventListener('click', onGesture);
      observer.disconnect();
    };
  }, []);

  return (
    <section className={styles.heroSection}>
      <div className={styles.heroPanel}>
        <video
          ref={videoRef}
          className={styles.heroVideo}
          preload="metadata"
          loop
          autoPlay
          muted
          playsInline
          controls={false}
          disablePictureInPicture
          aria-hidden
        >
          <source src={MOBILE_VIDEO} type="video/mp4" />
        </video>
        <div className={styles.heroOverlay} aria-hidden />
        <div className={styles.heroContent}>
          <div className={styles.copy}>
            <h1 className={styles.title}>{t('hero.title')}</h1>
            <p className={styles.subtitle}>{t('hero.subtitle')}</p>
          </div>
          <div className={pills.ctaRow}>
            <CustomLink
              className={`${pills.pill} ${pills.pillPrimary}`}
              href="/ring/buy"
              data-buttontype="why-uh hero buy"
            >
              {t('hero.buyCta')}
            </CustomLink>
            <CustomLink
              className={`${pills.pill} ${pills.pillUnderline}`}
              href={{ pathname: '/ring/buy', query: { tradeIn: '1' } }}
              data-buttontype="why-uh hero trade-in"
            >
              {t('hero.tradeInCta')}
            </CustomLink>
          </div>
        </div>
      </div>
    </section>
  );
};
